import React, { useState, useEffect } from 'react';
import {
    Box, Card, CardContent, Typography, Button, Chip, Grid, Stack,
    Divider, Alert, Skeleton,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Edit as EditIcon,
    Person as PersonIcon,
    ShoppingCart as OrderIcon,
    CheckCircle as ActiveIcon,
    Cancel as InactiveIcon,
    Visibility as ViewIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import customerService from '../../../services/customerService';
import orderService from '../../../services/orderService';
import AppDataGrid from '../../common/AppDataGrid';
import GridActionsCell from '../../common/GridActionsCell';
import { useCurrency } from '../../../hooks/useCurrency';

const fmtDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('fr-DZ', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const orderStatusColors = {
    DRAFT: 'default', CONFIRMED: 'success', PENDING: 'warning',
    PROCESSING: 'info', SHIPPED: 'primary', DELIVERED: 'success', CANCELLED: 'error',
    RETURN_REQUESTED: 'warning', EXCHANGE_REQUESTED: 'warning', RETURNED: 'secondary', EXCHANGED: 'secondary',
};
const paymentStatusColors = {
    PENDING: 'warning', PAID: 'success', FAILED: 'error', REFUNDED: 'default',
};

// Skeleton placeholder for the info card — same shape as the real
// Stack/Box(caption+value) rows below, just Skeleton instead of Typography.
const InfoCardSkeleton = () => (
    <Stack spacing={1.5}>
        {[...Array(4)].map((_, i) => (
            <Box key={i}>
                <Skeleton variant="text" width={80} height={16} />
                <Skeleton variant="text" width="60%" height={24} />
            </Box>
        ))}
    </Stack>
);

const CustomerDetails = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { t } = useTranslation();

    const { format: fmt } = useCurrency();
    const [customer, setCustomer] = useState(null);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [ordersLoading, setOrdersLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchCustomer();
        fetchOrders();
    }, [id]);

    const fetchCustomer = async () => {
        setLoading(true);
        try {
            const data = await customerService.getCustomerById(id);
            setCustomer(data);
        } catch (err) {
            setError(err.response?.data?.message || t('customers.fetchError'));
        } finally {
            setLoading(false);
        }
    };

    const fetchOrders = async () => {
        setOrdersLoading(true);
        try {
            const data = await orderService.getOrdersByCustomer(id);
            setOrders(data || []);
        } catch {
            setOrders([]);
        } finally {
            setOrdersLoading(false);
        }
    };

    // Every order this client ever made: all stores and the website.
    const orderColumns = [
        {
            field: 'orderNumber',
            headerName: t('orders.orderNumber'),
            flex: 1,
            minWidth: 170,
            cellRenderer: (params) => (
                <Typography variant="body2" fontWeight="medium" color="primary" component="span">
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'source',
            headerName: t('customers.orderSource'),
            width: 140,
            valueGetter: (params) => (params.data?.source === 'WEBSITE' ? t('orders.onlineOrder') : t('customers.inStoreOrder')),
        },
        { field: 'storeName', headerName: t('orders.store'), width: 150, valueFormatter: (params) => params.value || '—' },
        {
            field: 'status',
            headerName: t('common.status'),
            width: 150,
            cellRenderer: (params) => (
                <Chip
                    label={t(`orders.status.${params.value}`, params.value)}
                    size="small"
                    color={orderStatusColors[params.value] || 'default'}
                />
            ),
        },
        {
            field: 'paymentStatus',
            headerName: t('orders.paymentStatus'),
            width: 150,
            cellRenderer: (params) => (
                <Chip
                    label={t(`orders.paymentStatuses.${params.value}`, params.value)}
                    size="small"
                    color={paymentStatusColors[params.value] || 'default'}
                    variant="outlined"
                />
            ),
        },
        {
            field: 'totalAmount',
            headerName: t('orders.totalAmount'),
            width: 150,
            cellRenderer: (params) => fmt(params.value),
        },
        {
            field: 'orderedAt',
            headerName: t('common.date'),
            width: 180,
            sort: 'desc',
            valueGetter: (params) => params.data?.orderedAt || params.data?.createdAt,
            cellRenderer: (params) => fmtDate(params.value),
        },
        {
            field: 'actions',
            headerName: t('common.actions'),
            width: 100,
            sortable: false,
            cellRenderer: (params) => (
                <GridActionsCell
                    actions={[
                        {
                            key: 'view',
                            icon: ViewIcon,
                            title: t('common.details'),
                            color: 'info',
                            onClick: () => navigate(`/orders/${params.data.id}`),
                        },
                    ]}
                />
            ),
        },
    ];

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/customers')} sx={{ mt: 2 }}>
                    {t('common.back')}
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/customers')}>
                        {t('common.back')}
                    </Button>
                    {loading ? (
                        <Skeleton variant="text" width={220} height={40} />
                    ) : (
                        <>
                            <Typography variant="h4" fontWeight="bold">
                                {customer.firstName} {customer.lastName}
                            </Typography>
                            <Chip
                                icon={customer.isActive ? <ActiveIcon /> : <InactiveIcon />}
                                label={customer.isActive ? t('common.active') : t('common.inactive')}
                                color={customer.isActive ? 'success' : 'default'}
                                size="small"
                            />
                        </>
                    )}
                </Box>
                <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    disabled={loading}
                    onClick={() => navigate(`/customers/edit/${id}`)}
                >
                    {t('common.edit')}
                </Button>
            </Box>

            <Grid container spacing={3}>
                {/* Left: customer info */}
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <PersonIcon color="primary" />
                                <Typography variant="h6">{t('customers.customer')}</Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            {loading ? (
                                <InfoCardSkeleton />
                            ) : (
                                <Stack spacing={1.5}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">{t('customers.email')}</Typography>
                                        <Typography variant="body1" fontWeight="medium">{customer.email || '—'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">{t('customers.phone')}</Typography>
                                        <Typography variant="body1" fontWeight="medium">{customer.phone || '—'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">{t('customers.source')}</Typography>
                                        <Typography variant="body1" fontWeight="medium">
                                            {customer.source === 'WEBSITE' ? t('customers.sourceWebsite') : t('customers.sourceStore')}
                                        </Typography>
                                    </Box>
                                    {customer.email && (
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">{t('customers.emailStatus')}</Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <Chip
                                                    label={customer.emailVerifiedAt ? t('customers.emailConfirmed') : t('customers.emailNotConfirmed')}
                                                    color={customer.emailVerifiedAt ? 'success' : 'warning'}
                                                    variant={customer.emailVerifiedAt ? 'filled' : 'outlined'}
                                                    size="small"
                                                />
                                                {customer.emailVerifiedAt && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {t('customers.emailVerifiedAt')} {fmtDate(customer.emailVerifiedAt)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    )}
                                </Stack>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Right: order history */}
                <Grid item xs={12} md={8}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <OrderIcon color="primary" />
                                <Typography variant="h6">
                                    {t('nav.orders')}
                                    {!ordersLoading && (
                                        <Chip
                                            label={orders.length}
                                            size="small"
                                            sx={{ ml: 1 }}
                                            color="secondary"
                                            variant="outlined"
                                        />
                                    )}
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            <AppDataGrid
                                rows={orders}
                                columns={orderColumns}
                                loading={ordersLoading}
                                pageSize={10}
                                pageSizeOptions={[10, 25, 50]}
                                getRowId={(row) => row.id}
                            />
                            {!ordersLoading && orders.length === 0 && (
                                <Alert severity="info" icon={false} sx={{ mt: 2 }}>
                                    {t('common.noData')}
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default CustomerDetails;
